import type { ReferencedEntry } from 'contentful';
import { flattenContentfulData, FlatEntry } from 'services/cms/flattenContentful';
import type { AppType } from 'services/cms/typesUtil';
import { isEntry } from 'utils/EntryUtil';

/**
 * Transforms a Contentful entry to an app-type object
 *
 * @remarks
 * Clean a provided entry to remove all Contentful related fields returns a
 * simplified object to use with UI-components. Additonally this calls
 * contentType specific transforms if one exists for the provided content type
 *
 * @param entry The entry for the content
 * @param props Additional props object to merge with the provided entry
 * @returns
 * This primarily returns {@link AppType.Content} to represent the app-type
 * contracts if {@link flattenContentfulData} fails to flatten the data, this
 * will return undefined
 */
export const transformContentfulObject = <T = any>(
  entry: ReferencedEntry<T>,
  props?: { [key: string]: any },
): AppType.Content | undefined => {
  // If it's a Contentful object that has not been flattened, flatten the entry
  const flatEntry = isEntry(entry) ? flattenContentfulData<T>(entry) : (entry as FlatEntry<any>);
  if (!flatEntry) return;

  // Check if entry has a contentType. If not, return as-is
  const contentType = flatEntry.contentType;
  if (!contentType) return flatEntry as AppType.Content;

  // Apply common transformation
  const transformedEntry = commonTransform({ ...flatEntry, ...props });

  return transformedEntry;
};

/**
 * Apply common CMS object transformation to the supplied entry
 *
 * @remarks
 * Iterate through the keys of the supplied object. This method extracts
 * CMS-related keys to _sys and _attributes, therefor making all top-level
 * properties to be used with downstream components.
 *
 * @param objectToTransform The object that will be transformed
 * @returns
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function commonTransform(objectToTransform: any): AppType.Content {
  const SYS_KEYS = ['contentType', 'id', 'internalName'];
  const ATTRIBUTE_KEYS = ['style', 'className'];
  const transformedObject = {
    _sys: {},
    ...objectToTransform,
  };

  // Filter props
  Object.keys(transformedObject).forEach(key => {
    if (key === '_sys' || key === '_attributes') return;

    // Iterate if an array of entries
    if (Array.isArray(transformedObject[key])) {
      transformedObject[key] = transformedObject[key].map((value: any) => {
        if (!isContentTypeObject(value)) return value;
        return transformContentfulObject(value);
      });

      // If an content type object itself, clean children
    } else if (isContentTypeObject(transformedObject[key])) {
      transformedObject[key] = transformContentfulObject(transformedObject[key]);

      // If the key is attribute based, move to _attributes
    } else if (key.startsWith('data-') || ATTRIBUTE_KEYS.indexOf(key) >= 0) {
      if (!transformedObject._attributes) transformedObject._attributes = {};
      transformedObject._attributes[key] = transformedObject[key];
      delete transformedObject[key];

      // If the key is system/cms based, move to _sys
    } else if (SYS_KEYS.indexOf(key) >= 0) {
      transformedObject._sys[key] = transformedObject[key];
      delete transformedObject[key];
    }
  });

  return transformedObject;
}

/**
 * Tests if the provided object is a CMS object
 *
 * @param object
 * @returns
 * Returns `true` if the provided object represents a Contentful object
 */
function isContentTypeObject(object: any): boolean {
  return typeof object === 'object' && object.contentType;
}
